import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Check, ArrowRight, Wrench, Settings, Car } from 'lucide-react';
import { companyInfo } from '../data/mockData';
import { useTheme } from '../context/ThemeContext';
import SEO, { createServiceSchema } from '../components/SEO';

const mechanicalServices = [
  "Engine diagnostics and repairs",
  "Brake system service and repairs",
  "Suspension and steering",
  "Transmission service",
  "Clutch repairs and replacement",
  "Cooling system service",
  "Exhaust system repairs",
  "General vehicle servicing"
];

const MechanicalRepairsPage = () => {
  const { isDark } = useTheme();

  const serviceSchema = createServiceSchema(
    'Mechanical Repairs & Servicing',
    'Complete mechanical repair services including engine diagnostics, brake repairs, suspension, transmission, and general vehicle servicing in Murray Bridge, South Australia.',
    'https://www.jprcustoms.com.au/mechanical-repairs'
  );

  return (
    <div className={`min-h-screen ${isDark ? 'bg-neutral-950' : 'bg-white'}`}>
      <SEO 
        title="Mechanical Repairs & Vehicle Servicing"
        description="Expert mechanical repairs in Murray Bridge SA. Engine diagnostics, brake repairs, suspension, transmission, clutch repairs, cooling system, exhaust repairs. All makes & models. Call +61 417 119 544."
        keywords="mechanical repairs Murray Bridge, car mechanic, engine repairs, brake repairs, suspension, transmission service, car servicing, auto repairs"
        canonical="/mechanical-repairs"
        structuredData={serviceSchema}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=1920&q=80)` }}
          >
            <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-r from-neutral-950/95 via-neutral-950/80 to-neutral-950/60' : 'bg-gradient-to-r from-white/95 via-white/85 to-white/70'}`} />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 pt-44">
            <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 ${isDark ? 'text-white' : 'text-neutral-900'}`}>
              Professional<br />
              <span className="text-lime-500">Mechanical Repairs</span>
            </h1>
            <p className={`text-xl max-w-2xl mb-8 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
              Expert mechanical services to keep your vehicle running smoothly and safely.
            </p>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center gap-2 px-8 py-4 bg-lime-500 text-black font-semibold hover:bg-lime-400 transition-all duration-300 rounded-lg"
            >
              <Phone className="w-5 h-5" />
              Get a Free Quote
            </a>
          </div>
        </section>

        {/* Features */}
        <section className={`py-16 ${isDark ? 'bg-neutral-800' : 'bg-neutral-50'}`}>
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className={`p-8 rounded-xl text-center ${isDark ? 'bg-neutral-900' : 'bg-white shadow-lg'}`}>
                <div className="w-16 h-16 bg-lime-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Wrench className="w-8 h-8 text-black" />
                </div>
                <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Expert Technicians</h3>
                <p className={isDark ? 'text-neutral-400' : 'text-neutral-600'}>Qualified mechanics with years of experience.</p>
              </div>
              <div className={`p-8 rounded-xl text-center ${isDark ? 'bg-neutral-900' : 'bg-white shadow-lg'}`}>
                <div className="w-16 h-16 bg-lime-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Settings className="w-8 h-8 text-black" />
                </div>
                <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Quality Parts</h3>
                <p className={isDark ? 'text-neutral-400' : 'text-neutral-600'}>We use only high-quality replacement parts.</p>
              </div>
              <div className={`p-8 rounded-xl text-center ${isDark ? 'bg-neutral-900' : 'bg-white shadow-lg'}`}>
                <div className="w-16 h-16 bg-lime-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Car className="w-8 h-8 text-black" />
                </div>
                <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-neutral-900'}`}>All Makes & Models</h3>
                <p className={isDark ? 'text-neutral-400' : 'text-neutral-600'}>We service all vehicle makes and models.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className={`py-20 ${isDark ? 'bg-neutral-900' : 'bg-white'}`}>
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-lime-500 mb-6">Complete Mechanical Services</h2>
                <p className={`mb-6 leading-relaxed ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                  At JPR Customs, we offer comprehensive mechanical repair services to keep your vehicle in top condition. Our skilled technicians can diagnose and repair any mechanical issue, from routine maintenance to complex repairs.
                </p>
                <p className={`mb-8 leading-relaxed ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                  We pride ourselves on honest, transparent service. We'll always explain what's wrong with your vehicle and provide a detailed quote before any work begins.
                </p>
                <h3 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Our Mechanical Services Include:</h3>
                <ul className="space-y-3">
                  {mechanicalServices.map((service, index) => (
                    <li key={index} className={`flex items-center gap-3 ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}>
                      <Check className="w-5 h-5 text-lime-500 flex-shrink-0" />
                      <span>{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-4">
                <img 
                  src="https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=800&q=80" 
                  alt="Mechanical repairs"
                  className="w-full h-64 object-cover rounded-xl"
                />
                <img 
                  src="https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800&q=80" 
                  alt="Engine work"
                  className="w-full h-64 object-cover rounded-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className={`py-16 ${isDark ? 'bg-neutral-800' : 'bg-neutral-50'}`}>
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-neutral-900'}`}>Need Mechanical Repairs?</h2>
            <p className={`mb-8 ${isDark ? 'text-neutral-400' : 'text-neutral-600'}`}>Contact us today for a free diagnostic assessment.</p>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center gap-2 px-8 py-4 bg-lime-500 text-black font-semibold hover:bg-lime-400 transition-all duration-300 rounded-lg"
            >
              Call {companyInfo.phone}
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default MechanicalRepairsPage;
